import React, { useEffect, useMemo, useRef, useState } from 'react';
import { LuSearch, LuX } from 'react-icons/lu';

/** Normalize for tolerant matching. */
const normalize = (value = '') =>
  String(value)
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const compact = (value = '') => normalize(value).replace(/\s+/g, '');

const matchesCommand = (cmd, rawQuery) => {
  const q = normalize(rawQuery);
  if (!q) return true;

  const haystack = normalize(
    [cmd.label, cmd.group, ...(cmd.keywords || [])].filter(Boolean).join(' ')
  );
  const hayCompact = compact(haystack);
  const qCompact = compact(rawQuery);

  if (haystack.includes(q) || (qCompact && hayCompact.includes(qCompact))) {
    return true;
  }

  return q
    .split(/\s+/)
    .filter(Boolean)
    .every((token) => haystack.includes(token) || hayCompact.includes(token));
};

const groupOrder = ['Projects', 'Workspaces', 'Actions', 'Define', 'Build', 'Analyze', 'Configure', 'Project', 'Workspace', 'Pages'];

const CommandPalette = ({ open, onClose, commands = [] }) => {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setActive(0);
      return undefined;
    }
    const t = setTimeout(() => inputRef.current?.focus(), 40);
    return () => clearTimeout(t);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim();
    if (!q) return commands;
    return commands.filter((c) => matchesCommand(c, q));
  }, [commands, query]);

  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach((cmd) => {
      const g = cmd.group || 'Pages';
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(cmd);
    });
    const keys = [...map.keys()].sort((a, b) => {
      const ia = groupOrder.indexOf(a);
      const ib = groupOrder.indexOf(b);
      if (ia === -1 && ib === -1) return a.localeCompare(b);
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
    return keys.map((key) => ({ group: key, items: map.get(key) }));
  }, [filtered]);

  const flat = useMemo(() => grouped.flatMap((g) => g.items), [grouped]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive((i) => Math.min(i + 1, Math.max(flat.length - 1, 0)));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActive((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' && flat[active]) {
        e.preventDefault();
        flat[active].onSelect?.();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, flat, active]);

  if (!open) return null;

  let runningIndex = -1;

  return (
    <div className="rt2-cmd-overlay" onClick={onClose} role="presentation">
      <div
        className="rt2-cmd"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Search"
      >
        <div className="rt2-cmd__input-row">
          <LuSearch size={18} className="rt2-cmd__icon" aria-hidden />
          <input
            ref={inputRef}
            type="search"
            className="rt2-cmd__input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search projects, workspaces, pages…"
            aria-label="Search"
            autoComplete="off"
            spellCheck={false}
          />
          {query ? (
            <button
              type="button"
              className="rt2-cmd__clear"
              aria-label="Clear"
              onClick={() => setQuery('')}
            >
              <LuX size={14} />
            </button>
          ) : (
            <kbd className="rt2-cmd__esc">esc</kbd>
          )}
        </div>

        <div className={`rt2-cmd__list${flat.length === 0 ? ' rt2-cmd__list--empty' : ''}`}>
          {flat.length === 0 ? (
            <div className="rt2-cmd__empty">
              <p className="rt2-cmd__empty-title">No matches</p>
              <p className="rt2-cmd__empty-desc">
                {query.trim()
                  ? `Nothing found for “${query.trim()}”. Try another project or workspace name.`
                  : 'Start typing to search projects, workspaces, and pages.'}
              </p>
            </div>
          ) : (
            grouped.map(({ group, items }) => (
              <div key={group} className="rt2-cmd__group">
                <div className="rt2-cmd__group-label">{group}</div>
                {items.map((cmd) => {
                  runningIndex += 1;
                  const idx = runningIndex;
                  return (
                    <button
                      key={`${group}-${cmd.label}-${idx}`}
                      type="button"
                      className={`rt2-cmd__item${idx === active ? ' rt2-cmd__item--active' : ''}`}
                      onClick={() => {
                        cmd.onSelect?.();
                        onClose();
                      }}
                      onMouseEnter={() => setActive(idx)}
                    >
                      <span className="rt2-cmd__item-label">{cmd.label}</span>
                      <span className="rt2-cmd__hint">{group}</span>
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="rt2-cmd__footer">
          <span>
            <kbd>↑↓</kbd> navigate
          </span>
          <span>
            <kbd>↵</kbd> open
          </span>
          <span>
            <kbd>esc</kbd> close
          </span>
          {flat.length > 0 && <span className="rt2-cmd__count">{flat.length} results</span>}
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
